import { Query } from '@apollo/client/react/components';
import { KeySet, keySetToStringList } from '@tableau/data-structures';
import { DropdownButtonType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';

import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { ColumnDetailsByLuidDocument, ColumnDetailsByLuidQuery, ColumnDetailsByLuidQueryVariables } from '../../../../../gen-src/queries';
import { ApiRequestsContext, assertUnreachable, FeatureFlagsContext, RemoteTypeMappings, RenderDataQualityDialogParams, UIActionsContext } from '../../../data-catalog-shared';
import { ColumnDetailsDialogQueryWrapper } from '../ColumnDetails/ColumnDetailsDialog';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { ReRenderPlacePageContext } from '../placePage/ReRenderPlacePageContext';
import { RenderQueryResult } from '../renderQueryResult';
import { columnActionsMenuStyle } from '../style/ColumnAndFieldDetailsStyles';
import { ColumnActions } from './InlineActionsMenuHelper';

const noop = () => { return; };

export type ColumnActionsMenuProps = {
  readonly columnIds: KeySet;
  readonly tableVizportalId: string | null;
};

export function ColumnActionsMenu(props: ColumnActionsMenuProps): JSX.Element | null {
  const [action, setAction] = React.useState<ColumnActions | null>(null);
  const [isDialogShowing, setIsDialogShowing] = React.useState(false);
  const columnIdsStringList = keySetToStringList(props.columnIds);

  const { rerender } = React.useContext(ReRenderPlacePageContext);
  const { canUserSetColumnDescription } = React.useContext(ApiRequestsContext);
  const { renderDataQualityDialog, renderSensitiveLabelsDialog } = React.useContext(UIActionsContext);
  const { customSensitivityLabelsEnabled } = React.useContext(FeatureFlagsContext);
  const [canUpdate, setCanUpdate] = React.useState(false);

  React.useEffect(
    () => {
      canUserSetColumnDescription({ tableVizportalId: props.tableVizportalId ?? '' })
        .then(r => setCanUpdate(r))
        .catch(() => { return; });
    },
    [canUserSetColumnDescription, props.tableVizportalId]
  );

  const columnId = columnIdsStringList && columnIdsStringList[0] ? columnIdsStringList[0] : null;

  if (!columnId) {
    return null;
  }

  return (
    <Query<ColumnDetailsByLuidQuery, ColumnDetailsByLuidQueryVariables>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      query={ColumnDetailsByLuidDocument}
      variables={{ columnId }}
    >
      {result => <RenderQueryResult result={result} renderers={{
        renderData: data => {
          const column = data && data.columns && data.columns[0] ? data.columns[0] : null;

          if (!column) {
            return null
          }

          const qualityActionItem: ListItem = {
            value: ColumnActions.QualityWarning,
            content: Messages.DataQualityWarningAction(),
            disabled: !canUpdate || !column.luid,
          };

          const sensitiveLabelActionItem: ListItem = {
            value: ColumnActions.SensitiveLabels,
            content: Messages.SensitiveLabelAction(),
            disabled: !canUpdate || !column.luid,
          }

          const columnActionItems: ListItem[] = [
            {
              value: ColumnActions.Tag,
              content: Messages.TagAction(),
              disabled: !canUpdate,
            },
            {
              value: ColumnActions.Description,
              content: Messages.EditDescriptionAction(),
              disabled: !canUpdate,
            },
            { separator: true },
            qualityActionItem
          ];

          const renderDialogParams: RenderDataQualityDialogParams = {
            content: {
              contentId: column.luid,
              contentName: column.name ?? '',
              contentType: 'column',
              contentIcon: (<RemoteTypeIcon remoteType={column.remoteType} />),
              contentIconTitle: RemoteTypeMappings[column.remoteType](),
            },
            dismiss: () => {
              rerender();
            },
          };

          return (<>
            <SingleSelectDropdown
              type={DropdownButtonType.Line}
              label={''}
              list={<List items={customSensitivityLabelsEnabled ? columnActionItems.concat(sensitiveLabelActionItem) : columnActionItems} />}
              testId={'columns-actions-menu'}
              onChange={(value) => {
                switch (value) {
                  case ColumnActions.Description: case ColumnActions.Tag:
                    setIsDialogShowing(true);
                    setAction(value);
                    break;
                  case ColumnActions.QualityWarning:
                    if (column.luid) {
                      renderDataQualityDialog(renderDialogParams).catch(_ => { return; })
                    }
                    break;
                   case ColumnActions.SensitiveLabels:
                    renderSensitiveLabelsDialog(renderDialogParams).catch(_ => { return; })
                    break;
                   default:
                    assertUnreachable(value);
                    throw new Error('Unreachable - unknown column action in dropdown menu'); 
                }
              }}
              style={columnActionsMenuStyle}
            >
              {Messages.Actions()}
            </SingleSelectDropdown>

            {
              isDialogShowing && (
                <ColumnDetailsDialogQueryWrapper
                  columnId={columnIdsStringList[0]}
                  tableVizportalId={props.tableVizportalId}
                  onClose={() => setIsDialogShowing(false)}
                  afterDescriptionAndTagsChange={noop}
                  detailsAction={action ?? undefined}
                />
              )
            }
          </>);
        }
      }} />}
    </Query>
  );
}
